import * as sentry from '../../src/utils/sentry'
import type { SyncProcedure } from '../../../app-core/lib/procedure/Procedure'
import { Acti_cancelActivity } from '../Acti/Acti.cancelActivity'
import { Acti_startActivity } from '../Acti/Acti.startActivity'
import { DS_getData, DS_setData } from '../DS/DS.Data'
import { Flow_reportStart } from '../Flow/Flow.Report'
import * as LaunchDarkly from '../Util/Util.launchdarkly'
import { IDB_doneReports, IDB_procedures, IDB_scheduledInstances } from '../IDB/IDB.dexie'
import { IDB_locationKeysReadAll, IDB_locationRead } from '../IDB/IDB.table.location'
import { Plan_addEvent } from '../Plan/Plan.addEvent'
import type { Post_TPostId } from '../Post/Post.TPost'
import { Rpts_enoughSpaceToStartReport } from '../Rpts/Rpts.enoughSpaceToStartReport'
import { Rpts_openReportFetch } from '../Rpts/Rpts.OpenReport'
import { Rpts_noSpaceWarningDialog } from '../Rpts/Rpts.ui.NoSpaceWarning'
import { Rpts_overrideOpenReportDialog } from '../Rpts/Rpts.ui.OpenReportWarning'
import { UI_dialogOk } from '../UI/UI.Dialog'
import type { ScheduledInstanceSync } from '../../../shared/domain/scheduledInstance/types.no-deps'
import type { DueSoonIntervalUnit, OncePer, ScheduleFrequency } from '../../../shared/domain/schedule/types.no-deps'
import type { DateString, Nullable, UUID } from '../../../shared/lib/typeHelpers.no-deps'
import {
    isFlowWorkflow,
    type Workflow,
    isAppActivityWorkflow,
    type AppTrainingWorkflow,
} from '../../../app-core/lib/workflow/Workflow'
import type { SubjectType } from '../../../shared/domain/flows/types.no-deps'
import type { KnownUnit } from '../../../app-core/lib/unit/unit'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { Translations } from '../../../shared/i18n/Translations'
import type { Answer, PhotoUrls, ReportClientId, ReportDetailMap } from '../../../shared/domain/reports/types.no-deps'
import { isRestrictedUser } from '../../../shared/domain/users/permissions'
import { Rpts_NoAccessWarningDialog } from '../Rpts/Rpts.ui.NoAccessWarning'
import { doesUserHavePermission } from '../User/permissions'
import type { UnitWorkflowParameter } from '../../../shared/domain/UnitWorkflowParameter/types.no-deps'

// Todo.ScheduledInstanceDTO (Data Transfer Object)
export type Todo_ScheduledInstanceDTO = ScheduledInstanceSync
export interface TodoTeam {
    name: string
    id: number
}

export type Todo_OriginatingReport = {
    report: number
    reportClientId: ReportClientId
    commentCount: number
    workflowName: string
    endDate: DateString
    author: number
    post: Post_TPostId
    requiresSignature: boolean
    answers?: Answer[]
    workflow?: number
    workflowVersion?: number
    subjectUnitId?: number
    reportDetails?: ReportDetailMap
    photoUrls?: PhotoUrls
    isConfidential?: boolean
    title?: Nullable<string>
}

// Todo.ScheduledInstanceDPO (Data Persistance Object)
export type Todo_ScheduledInstanceDPO = {
    id: UUID
    schedule: UUID
    workflow: number
    procedure: number
    subjectUnitId: number
    startDate: number
    dueDate: number
    isDeleted: boolean
    lastModifiedAt: Date
    occurrences: number
    frequency: ScheduleFrequency
    showOverdue: boolean
    description: string
    origin?: Todo_OriginatingReport
    ancestorReports?: ReportClientId[]
    isAction: boolean
    createdBy: number
    oncePer: OncePer
    teams: TodoTeam[]
    users: number[]
    isVisibleAccordingToCondition?: boolean
    shouldBeVisibleOnToDoList?: boolean
    isAssignedToUser?: boolean
    reportSubjectUnitId?: number
    parameters?: UnitWorkflowParameter[]
    dueSoonIntervalUnit?: DueSoonIntervalUnit
    dueSoonIntervalQuantity?: number
}

export type Todo_ScheduledInstance = {
    id: UUID
    schedule: UUID
    workflow: Workflow
    workflowIsMissing: boolean
    subjectUnit: KnownUnit
    procedure: SyncProcedure
    startDate: Date
    dueDate: Date
    frequency: ScheduleFrequency
    remainingOccurences: number
    showOverdue: boolean
    description: string
    origin?: Todo_OriginatingReport
    ancestorReports?: ReportClientId[]
    isAction: boolean
    isAssignedToUser: boolean
    createdBy: number
    oncePer: OncePer
    missingTrainingRecommendations: AppTrainingWorkflow[]
    teams: TodoTeam[]
    users: number[]
    isVisibleAccordingToCondition?: boolean
    parameters?: UnitWorkflowParameter[]
}

export interface Todo_ScheduledInstances {
    overdue: Todo_ScheduledInstance[]
    available: Todo_ScheduledInstance[]
    upcoming: Todo_ScheduledInstance[]
}

/**
 * Todo.toScheduledInstanceDPO maps from the server type to idb type
 */
export function Todo_toScheduledInstanceDPO(scheduledInstance: Todo_ScheduledInstanceDTO): Todo_ScheduledInstanceDPO {
    return {
        id: scheduledInstance.id,
        schedule: scheduledInstance.schedule,
        workflow: scheduledInstance.workflow || -1,
        subjectUnitId: scheduledInstance.subjectUnitId || -1,
        procedure: scheduledInstance.procedure || -1,
        startDate: +scheduledInstance.startDate,
        dueDate: +scheduledInstance.dueDate,
        isDeleted: scheduledInstance.isDeleted,
        lastModifiedAt: scheduledInstance.lastModifiedAt,
        occurrences: scheduledInstance.occurrences,
        frequency: scheduledInstance.frequency,
        showOverdue: scheduledInstance.showOverdue,
        description: scheduledInstance.description,
        origin: scheduledInstance.origin
            ? {
                  ...scheduledInstance.origin,
                  post: scheduledInstance.origin.post as Post_TPostId,
              }
            : null,
        ancestorReports: (scheduledInstance?.ancestorReports as ReportClientId[]) || [],
        isAction: scheduledInstance.isAction,
        createdBy: scheduledInstance.createdBy,
        oncePer: scheduledInstance.oncePer,
        teams: scheduledInstance.teams,
        users: scheduledInstance.users,
        isVisibleAccordingToCondition: scheduledInstance.isVisibleAccordingToCondition,
        shouldBeVisibleOnToDoList: scheduledInstance.shouldBeVisibleOnToDoList,
        isAssignedToUser: scheduledInstance.isAssignedToUser,
        reportSubjectUnitId: scheduledInstance.reportSubjectUnitId,
        parameters: scheduledInstance?.parameters || [],
        dueSoonIntervalUnit: scheduledInstance.dueSoonIntervalUnit,
        dueSoonIntervalQuantity: scheduledInstance.dueSoonIntervalQuantity,
    }
}

/**
 * Delete scheduled instances that have been completed and whose due date is older than the specified {@link dueDateThreshold}
 *
 * @param dueDateThreshold Only scheduled instances with a due date older than this should be considered for deletion.
 */
export async function Todo_scheduledInstancesPrune(dueDateThreshold: Date) {
    try {
        const reportCountByInstanceIdMap = await getReportCountsByInstanceId()
        const getReportCount = (instanceId: string) =>
            reportCountByInstanceIdMap.hasOwnProperty(instanceId) ? reportCountByInstanceIdMap[instanceId] : 0

        const shouldDeleteOverdueInstance = (instance: Todo_ScheduledInstanceDPO) => {
            if (!instance.showOverdue) {
                return true
            }

            // `schedule.occurrences` is legacy... but still used.
            // Legacy schedules can have an `occurrences` value greater than 1, but for
            // new schedules it is effectively (but not always explicitly!) 1.
            const numReportsRequired = instance.occurrences ?? 1
            return getReportCount(instance.id) >= numReportsRequired
        }

        // N.B. all scheduled instances have a due date
        const deletedCount = await IDB_scheduledInstances()
            .where('dueDate')
            .below(dueDateThreshold.valueOf())
            .filter(shouldDeleteOverdueInstance)
            .delete()

        if (deletedCount > 0) {
            DS_setData('ScheduledInstancesPruned', () => ({
                scheduledInstancePruneTime: Date.now(),
            }))
        }
    } catch (error) {
        sentry.err(error, {})
    }
}

async function getReportCountsByInstanceId() {
    const completeReports = await IDB_doneReports()
        .orderBy('scheduledInstance') // Don't get items where scheduled instance is not present
        .toArray()
    const reportCountByInstanceIdMap: { [key: string]: number } = completeReports.reduce(
        (output: { [key: string]: number }, report) => ({
            ...output,
            [report.scheduledInstance]: output[report.scheduledInstance] ? output[report.scheduledInstance] + 1 : 1,
        }),
        {}
    )
    return reportCountByInstanceIdMap
}

export async function Todo_scheduledInstanceStart(
    logger: ClientLogger,
    scheduledInstance: Todo_ScheduledInstance,
    subjectUnitId?: number
) {
    try {
        // Infer the teamDoneFor the array of teams it was assigned to
        const teamDoneFor = scheduledInstance.teams[0]?.id

        const { serverPrefs, user } = DS_getData()

        // Check space
        if (!(await Rpts_enoughSpaceToStartReport(serverPrefs))) {
            Rpts_noSpaceWarningDialog(logger)
            return { hasStarted: false }
        }

        const procedureBubblesEnabled = LaunchDarkly.getFlagValue('procedure-bubbles', false)
        // Make sure if this is a restricted user they are allowed to start the workflow, which depends
        // on what access they have to the procedure
        if (procedureBubblesEnabled) {
            if (isRestrictedUser(user.privs)) {
                const procedureId = scheduledInstance.workflow?.procedure?.id
                const procedure = await IDB_procedures().get(procedureId)

                if (!procedure?.isAssigned) {
                    Rpts_NoAccessWarningDialog(logger)
                    return { hasStarted: false }
                }
            }
        }

        // Start flow report
        if (isFlowWorkflow(scheduledInstance.workflow)) {
            await Flow_reportStart(logger, scheduledInstance.procedure.id, scheduledInstance.workflow, {
                scheduledInstance,
                teamDoneFor,
                subjectUnitId,
            })
            return
        }

        // Make sure it's okay to cancel any current report
        const openReport = await Rpts_openReportFetch()
        if (openReport != null) {
            const overrideReport = await Rpts_overrideOpenReportDialog(logger, openReport.location)
            if (!overrideReport) return { hasStarted: false }

            await Acti_cancelActivity()
        }

        // Pick the first location
        const [locationId] = await IDB_locationKeysReadAll()
        if (locationId == null) {
            noLocationsDialog()
            return { hasStarted: false }
        }

        const [location, event] = await Promise.all([
            // Read the location that we've chosen
            IDB_locationRead(locationId),
            // Prepare a calendar event for the report
            Plan_addEvent(
                logger,
                new Date(),
                locationId,
                scheduledInstance.workflow.id,
                scheduledInstance.procedure && scheduledInstance.procedure.id
            ),
        ])

        const origin = scheduledInstance.origin ? scheduledInstance.origin.report : undefined

        // Start the report
        await Acti_startActivity(logger, location, event.id, scheduledInstance, teamDoneFor, origin)
        return { hasStarted: true }
    } catch (error) {
        sentry.err(error, {})
        return { hasStarted: false }
    }
}

export function Todo_getSubjectType(scheduledInstance: Todo_ScheduledInstance): SubjectType {
    const { workflow } = scheduledInstance
    if (workflow == null || isAppActivityWorkflow(workflow)) return null
    return workflow.flow.subjectType
}

function noLocationsDialog() {
    const title = Translations.t('app:UI.common.somethingWentWrong')
    const body = Translations.t('app:Todo.ToDoPage.noLocation')
    UI_dialogOk(title, body)
}

export function Todo_isSimpleAction(scheduledInstance: Todo_ScheduledInstanceDPO) {
    // In app, if an action is simple (without workflow), the workflow is given value of -1
    return scheduledInstance.isAction && scheduledInstance.workflow < 0
}

export function Todo_canCurrentUserSeeOriginatingReport(originatingReport: Todo_OriginatingReport) {
    if (!originatingReport) {
        return false
    }

    if (!originatingReport.isConfidential) {
        return true
    }

    return (
        doesUserHavePermission(DS_getData().user, 'confidentialReports') ||
        originatingReport.author === DS_getData().user.id
    )
}
